export const CUSTOM_SIZE_UNIT_OPTIONS = [
  { title: "mm", id: 0 },
  { title: "cm", id: 1 },
  { title: "in", id: 2 },
];

export const CUSTOM_SIZE_DEFAULT_UNIT = CUSTOM_SIZE_UNIT_OPTIONS[0];
export const CUSTOM_SIZE_MIN_MM = 100;
export const CUSTOM_SIZE_MAX_MM = 1500;

// The combined width plus height allowance is half the artwork perimeter, which
// is what frame SKU sizes are matched on. Where a country configures no short
// edge cap, the combined budget has to be given explicitly.
export const resolveCombinedLimitMm = (combinedMm, longMm, shortMm) => {
  const configured = Number(combinedMm);
  if (Number.isFinite(configured) && configured > 0) {
    return configured;
  }

  const long = Number(longMm);
  const short = Number(shortMm);
  if (
    shortMm == null ||
    !Number.isFinite(long) ||
    !Number.isFinite(short) ||
    short <= 0
  ) {
    return null;
  }

  return long + short;
};

export const formatMmToCm = (mm) => {
  const cm = mm / 10;
  return Number.isInteger(cm) ? String(cm) : String(Number(cm.toFixed(1)));
};

export const getCustomSizeUnitTitle = (unit = CUSTOM_SIZE_DEFAULT_UNIT) =>
  typeof unit === "string" ? unit : unit?.title || CUSTOM_SIZE_DEFAULT_UNIT.title;

export const getCustomSizeUnitOption = (unit = CUSTOM_SIZE_DEFAULT_UNIT) => {
  const unitTitle = getCustomSizeUnitTitle(unit);
  return (
    CUSTOM_SIZE_UNIT_OPTIONS.find((option) => option.title === unitTitle) ||
    CUSTOM_SIZE_DEFAULT_UNIT
  );
};

export const parseCustomSizeValue = (value) => {
  if (value === "" || value == null) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

export const convertCustomSizeToMm = (value, unit = CUSTOM_SIZE_DEFAULT_UNIT) => {
  const parsedValue = parseCustomSizeValue(value);
  if (parsedValue == null) {
    return null;
  }

  switch (getCustomSizeUnitTitle(unit)) {
    case "cm":
      return parsedValue * 10;
    case "in":
      return parsedValue * 25.4;
    default:
      return parsedValue;
  }
};

export const getCustomSizeMetrics = ({
  width,
  height,
  unit = CUSTOM_SIZE_DEFAULT_UNIT,
}) => {
  const parsedWidth = parseCustomSizeValue(width);
  const parsedHeight = parseCustomSizeValue(height);
  const widthMm = convertCustomSizeToMm(width, unit);
  const heightMm = convertCustomSizeToMm(height, unit);

  return {
    width: parsedWidth,
    height: parsedHeight,
    widthMm,
    heightMm,
    unitTitle: getCustomSizeUnitTitle(unit),
    ratio:
      parsedWidth != null && parsedHeight != null ? parsedWidth / parsedHeight : null,
    longestInches:
      widthMm != null && heightMm != null ? Math.max(widthMm, heightMm) / 25.4 : null,
  };
};

export const getCustomSizeValidationState = ({
  width,
  height,
  unit = CUSTOM_SIZE_DEFAULT_UNIT,
  minMm = CUSTOM_SIZE_MIN_MM,
  maxMm = CUSTOM_SIZE_MAX_MM,
  maxShortMm = null,
  maxCombinedMm = null,
}) => {
  const metrics = getCustomSizeMetrics({ width, height, unit });
  const hasShortLimit = maxShortMm != null;
  const hasCombinedLimit = maxCombinedMm != null;

  let valid = true;
  let message = "";

  if (metrics.widthMm == null || metrics.heightMm == null) {
    valid = false;
  } else {
    const longMm = Math.max(metrics.widthMm, metrics.heightMm);
    const shortMm = Math.min(metrics.widthMm, metrics.heightMm);
    const combinedMm = metrics.widthMm + metrics.heightMm;

    if (longMm > maxMm || (hasShortLimit && shortMm > maxShortMm)) {
      valid = false;
      message = hasShortLimit
        ? `Size must be no greater than ${formatMmToCm(maxMm)} x ${formatMmToCm(
            maxShortMm,
          )}cm. Please contact us for quotes at larger sizes.`
        : `Each length must be no greater than ${formatMmToCm(
            maxMm,
          )}cm. Please contact us for quotes at larger sizes.`;
    } else if (hasCombinedLimit && combinedMm > maxCombinedMm) {
      valid = false;
      message = `Width and height can total no more than ${formatMmToCm(
        maxCombinedMm,
      )}cm. Please contact us for quotes at larger sizes.`;
    } else if (metrics.widthMm < minMm || metrics.heightMm < minMm) {
      valid = false;
      message = `Each length must be no less than ${formatMmToCm(
        minMm,
      )}cm or ${minMm}mm`;
    }
  }

  return {
    ...metrics,
    valid,
    message,
    minMm,
    maxMm,
    maxShortMm,
    maxCombinedMm,
  };
};

export const flipCustomSizeDimensions = ({ width, height }) => ({
  width: height,
  height: width,
});
